import { useEffect, useMemo, useState } from "react";
import "./App.css";
import NavBar from "./Components/NavBar";
import Footer from "./Components/Footer";
import Homepage from "./pages/Homepage";
import Properties from "./pages/Properties";
import PropertyDetails from "./pages/PropertyDetails";
import AboutUs from "./pages/AboutUs";
import EricNur from "./pages/EricNur";
import Services from "./pages/Services";
import FAQ from "./pages/FAQ";
import Questionnaire from "./pages/Questionnaire";
import Contact from "./pages/Contact";
import PageNotFound from "./pages/PageNotFound";
import { properties } from "./data/properties";
import AdminApp, { isAdminRoute } from "./admin/AdminApp";
import { getNavigation, getSiteSettings } from "./api/content";

const defaultSiteSettings = {
  logo_image_url: "",
  logo_alt_text: "Trophy NNN Assets logo",
  logo_title: "Trophy NNN Assets",
  logo_subtitle: "American Realty Network LLC",
  primary_phone: "561-702-3795",
  primary_cta_label: "Call or Text",
  primary_cta_link: "/contact",
  footer_brand: "Cap Rate Experts",
  footer_description:
    "Investor-focused advisory and brokerage support for NNN and Absolute NNN acquisitions across the United States, including high CAP investment opportunities and 1031 exchange solutions.",
  footer_copyright: "© 2026 American Realty Network LLC. All rights reserved.",
};

const defaultNavigation = [
  { label: "Home", path: "/" },
  { label: "About us", path: "/about" },
  { label: "Questionnaire", path: "/questionnaire" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
  { label: "ERIC NUR", path: "/eric-nur" },
];

const getPath = () => window.location.pathname || "/";

const navigateTo = (path, setPath) => {
  if (path !== getPath()) {
    window.history.pushState({}, "", path);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setPath(path);
  }
};

function App() {
  const [path, setPath] = useState(getPath());
  const [siteSettings, setSiteSettings] = useState(defaultSiteSettings);
  const [navigationItems, setNavigationItems] = useState(defaultNavigation);

  useEffect(() => {
    const handlePopState = () => setPath(getPath());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadGlobals = async () => {
      try {
        const [settingsPayload, navigationPayload] = await Promise.all([getSiteSettings(), getNavigation()]);

        if (!ignore) {
          setSiteSettings((current) => ({ ...current, ...(settingsPayload || {}) }));
        }

        if (!ignore && Array.isArray(navigationPayload) && navigationPayload.length) {
          setNavigationItems(navigationPayload);
        }
      } catch {
        // keep local defaults when backend content is unavailable
      }
    };

    loadGlobals();

    return () => {
      ignore = true;
    };
  }, []);

  const selectedProperty = useMemo(() => {
    if (!path.startsWith("/properties/")) return null;
    const propertySlug = decodeURIComponent(path.replace("/properties/", ""));
    return (
      properties.find((property, index) => {
        const routeId = String(index + 1);
        return routeId === propertySlug || (property.propertyId || property.id) === propertySlug;
      }) || null
    );
  }, [path]);

  const sharedProps = {
    onNavigate: (nextPath) => navigateTo(nextPath, setPath),
  };

  if (isAdminRoute(path)) {
    return <AdminApp path={path} onNavigate={sharedProps.onNavigate} />;
  }

  const renderPage = () => {
    if (path === "/") return <Homepage {...sharedProps} />;
    if (path === "/properties") return <Properties {...sharedProps} />;
    if (path.startsWith("/properties/") && selectedProperty) {
      return <PropertyDetails property={selectedProperty} {...sharedProps} />;
    }
    if (path === "/about") return <AboutUs />;
    if (path === "/eric-nur") return <EricNur />;
    if (path === "/services") return <Services />;
    if (path === "/faq") return <FAQ />;
    if (path === "/questionnaire") return <Questionnaire />;
    if (path === "/contact") return <Contact />;
    return <PageNotFound {...sharedProps} />;
  };

  return (
    <div className="app-shell">
      <NavBar
        currentPath={path}
        onNavigate={sharedProps.onNavigate}
        siteSettings={siteSettings}
        navigationItems={navigationItems}
      />
      <main className="site-main">{renderPage()}</main>
      <Footer onNavigate={sharedProps.onNavigate} siteSettings={siteSettings} />
    </div>
  );
}

export default App;
